import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  DepartmentReportScope,
  DepartmentReportSection,
  buildDepartmentReportCsv,
  departmentReportFileName,
  departmentReportScopeLabel,
} from './departmentReport';

/**
 * Renders a department report to PDF or CSV.
 *
 * Extracted from DepartmentDetail so the operational view's department frame can produce the same
 * document. The CONTENTS are built (and tested) as plain data in departmentReport.ts; this is only
 * the paper. Two hand-written copies of ~80 lines of jsPDF layout is how the same report would end
 * up with a different header, a different scope line, or a differently-named file depending on
 * which screen the operator happened to be standing on.
 */

async function drawHeader(doc: jsPDF, departmentName: string, scope: DepartmentReportScope) {
  const W = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  // Best-effort: a missing logo must not stop the report. The 2s cap keeps a hung image request
  // from leaving the operator staring at a button that never responds.
  try {
    const logoImg = new Image();
    logoImg.src = '/panther-logo.png';
    await new Promise<void>(resolve => {
      logoImg.onload = () => {
        try { doc.addImage(logoImg, 'PNG', 14, 6, 20, 20); } catch { /* ignore */ }
        resolve();
      };
      logoImg.onerror = () => resolve();
      setTimeout(() => resolve(), 2000);
    });
  } catch { /* ignore */ }

  doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('ACCESS', 37, 18);
  doc.setLineWidth(0.5); doc.line(14, 30, W - 14, 30);
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text(`Relatório - ${departmentName}`, 14, 38);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(
    `Departamento: ${departmentName}  |  Escopo: ${departmentReportScopeLabel(scope)}  |  Gerado em: ${today}`,
    14, 44,
  );
}

export async function exportDepartmentReportPDF(
  departmentName: string,
  scope: DepartmentReportScope,
  sections: DepartmentReportSection[],
): Promise<void> {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await drawHeader(doc, departmentName, scope);

  let cursorY = 52;
  sections.forEach(section => {
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text(`${section.title} (${section.rows.length})`, 14, cursorY);
    cursorY += 4;

    if (section.rows.length === 0) {
      // Printed instead of skipped: an absent block reads as "not part of this report" rather
      // than "nothing to report".
      doc.setFontSize(9); doc.setFont('helvetica', 'italic');
      doc.text(section.emptyMessage, 14, cursorY + 3);
      cursorY += 12;
      return;
    }

    autoTable(doc, {
      startY: cursorY,
      head: [section.head],
      body: section.rows,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [17, 24, 39] },
    });
    cursorY = (doc as any).lastAutoTable.finalY + 10;
  });

  doc.save(departmentReportFileName(departmentName, scope, 'pdf'));
}

export function exportDepartmentReportCSV(
  departmentName: string,
  scope: DepartmentReportScope,
  sections: DepartmentReportSection[],
): void {
  const csv = buildDepartmentReportCsv(departmentName, sections);
  // The BOM is what makes Excel read the accents correctly; without it "Funcionários" arrives
  // mangled, which is the only way most people ever open this file.
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = departmentReportFileName(departmentName, scope, 'csv');
  a.click();
  URL.revokeObjectURL(url);
}
